import api from './config';

/**
 * feed-genre-filter — the user's style filter, server side.
 *
 * Local storage owns the FIRST PAINT (the feed must start filtered, with no
 * unfiltered flash), and this owns the truth that follows the account rather
 * than the install. Written once when the filter sheet closes, so one PUT per
 * filtering session, never one per chip.
 *
 * filter-sheet-cleanup — the recent-filters history is gone (G, 2026-10-12):
 * the contract is `{ styles }`, and a `history` an older back still sends is
 * dropped here rather than carried into the state.
 *
 * Optimistic, like `changeUserLanguage`: a network failure must never take away
 * a selection the user can already see applied.
 */

export interface FeedFilterPreference {
  styles: string[];
}

/** The stored preference, or `null` when it could not be read. */
export async function fetchFeedFilter(): Promise<FeedFilterPreference | null> {
  try {
    const res = await api.get('/users/me/feed-filter');
    return { styles: Array.isArray(res.data?.styles) ? res.data.styles : [] };
  } catch {
    // Offline, or the contract is not deployed yet: the local state stands.
    return null;
  }
}

export async function saveFeedFilter(styles: string[]): Promise<void> {
  try {
    await api.put('/users/me/feed-filter', { styles });
  } catch {
    // Deliberately swallowed: the selection is already applied locally and
    // persisted on the device. Failing loudly here would punish the user for
    // a network blip on a preference.
  }
}
